import { AppStore } from '@/core/services/store/app.store';
import {
    HttpClient,
    HttpErrorResponse,
    HttpHeaders,
    HttpParams,
    httpResource,
    HttpResourceRef
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
    IApiErrorResponse,
    IApiPaginatedStandardResponse,
    IApiPutResponse,
    IApiStandardResponse
} from '@core/models/api/api.models';
import { Console } from '@core/services/logger.service';
import { environment } from '@environments/environment';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

/**
 * Interfaccia per i dati di paginazione nella richiesta
 */
export interface IRequestPaginationData {
  per_page: number;
  page: number;
  sort_by: string;
  sort_order: string;
  search: string;
}

type ApiQueryParams = Record<string, string | number | boolean>;

export interface ApiGetResourceOptions<T> {
  defaultValue?: T;
  debugName?: string;
  /** Trasforma i dati dopo `parseApiResponse` (es. mapping dominio su GET Laravel). */
  transform?: (extracted: unknown) => T;
}

/**
 * Servizio per la gestione delle chiamate API HTTP.
 * Fornisce metodi per effettuare richieste GET, POST, PUT, PATCH, DELETE verso l'API.
 * Gestisce automaticamente la pulizia degli endpoint, la gestione degli errori,
 * il logging e l'autenticazione tramite Bearer token.
 */
@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private readonly log_label = 'ApiService';
  private readonly http = inject(HttpClient);
  private readonly appStore = inject(AppStore);
  readonly BASE_URL = environment.API_ENDPOINT || 'http://localhost:3000/api';

  // Log inizializzazione BASE_URL al primo utilizzo
  private readonly _initLog = (() => {
    Console.log(`[${this.log_label}] Initialized with BASE_URL: ${this.BASE_URL}`, {
      apiEndpoint: environment.API_ENDPOINT,
      fallback: 'http://localhost:3000/api',
      production: environment.production
    }, 'info');
  })();

  /**
   * Pulisce l'endpoint rimuovendo lo slash iniziale se presente
   * @param endpoint - L'endpoint da pulire
   * @returns L'endpoint pulito senza slash iniziale
   */
  private cleanEndpoint(endpoint: string): string {
    return endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
  }

  /**
   * Costruisce l'URL completo per l'endpoint
   */
  private getUrl(endpoint: string): string {
    const cleanedEndpoint = this.cleanEndpoint(endpoint);
    const fullUrl = `${this.BASE_URL}/${cleanedEndpoint}`;
    Console.log(`[${this.log_label}] Building URL - BASE_URL: ${this.BASE_URL}, endpoint: ${endpoint}, cleaned: ${cleanedEndpoint}, final: ${fullUrl}`, '', 'info');
    return fullUrl;
  }

  /**
   * Costruisce gli headers per le richieste
   */
  private getHeaders(customHeaders?: Record<string, string>): HttpHeaders {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    // Aggiungi token se presente nello store
    const tokenData = this.appStore.token();
    if (tokenData?.token) {
      headers = headers.set('Authorization', `Bearer ${tokenData.token}`);
    }

    // Aggiungi headers custom
    if (customHeaders) {
      Object.keys(customHeaders).forEach(key => {
        headers = headers.set(key, customHeaders[key]);
      });
    }

    return headers;
  }

  /**
   * Estrae i dati dalla risposta API.
   * Se esiste solo message e non data{}, ritorna message, altrimenti data{}
   */
  private parseApiResponse<T>(response: unknown): T {
    const body = response as { message?: unknown; data?: unknown };

    if (body.message !== undefined && body.data === undefined) {
      return body.message as T;
    }
    if (body.data !== undefined) {
      return body.data as T;
    }
    return response as T;
  }

  /**
   * Normalizza un errore HTTP nel formato IApiErrorResponse usato dall'app.
   * Utile anche per `httpResource.error()` dove non passa da RxJS catchError.
   */
  normalizeApiError(error: unknown): IApiErrorResponse {
    if (error instanceof HttpErrorResponse) {
      Console.log(`[${this.log_label}] Error occurred:`, error, 'err');

      const body = error.error;
      const isObjectBody = typeof body === 'object' && body !== null;
      const normalized: Record<string, unknown> = isObjectBody ? { ...(body as Record<string, unknown>) } : {};

      if (normalized['status_code'] == null) {
        normalized['status_code'] = error.status;
      }
      if (normalized['message'] == null && typeof body !== 'object') {
        normalized['message'] = error.message;
      }
      if (normalized['status'] == null) {
        normalized['status'] = error.status;
      }

      return normalized as unknown as IApiErrorResponse;
    }

    if (typeof error === 'object' && error !== null && 'status_code' in error) {
      return error as IApiErrorResponse;
    }

    return {
      message: error instanceof Error ? error.message : 'Errore sconosciuto',
      status_code: 0
    };
  }

  /**
   * Gestisce gli errori HTTP per le chiamate Observable
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    return throwError(() => this.normalizeApiError(error));
  }

  /**
   * Costruisce HttpParams da un oggetto.
   * Esclude parametri null, undefined e stringhe vuote (es. search='' non viene aggiunto).
   */
  private buildParams(params: ApiQueryParams): HttpParams {
    let httpParams = new HttpParams();
    Object.keys(params).forEach(key => {
      const val = params[key];
      if (val !== null && val !== undefined) {
        const str = val.toString();
        if (str !== '') {
          httpParams = httpParams.set(key, str);
        }
      }
    });
    return httpParams;
  }

  /**
   * Costruisce i query params per una richiesta paginata.
   */
  private buildPaginationParams(
    paginationData: IRequestPaginationData,
    params?: ApiQueryParams
  ): ApiQueryParams {
    const allParams: ApiQueryParams = {
      page: paginationData.page.toString(),
      per_page: paginationData.per_page.toString(),
      sort_by: paginationData.sort_by,
      sort_order: paginationData.sort_order,
      ...params
    };
    const searchTrimmed = typeof paginationData.search === 'string' ? paginationData.search.trim() : '';
    if (searchTrimmed !== '') {
      allParams['search'] = searchTrimmed;
    }
    return allParams;
  }

  /**
   * GET reattivo basato su `httpResource` (Angular 22).
   * Va creato in un injection context (servizio o campo di componente).
   *
   * @param endpoint - Factory reattiva: se ritorna `undefined` la richiesta non parte
   * @param params - Factory opzionale per query string
   * @param options - `defaultValue` evita `undefined` in loading; `debugName` per DevTools
   */
  getResource<T>(
    endpoint: () => string | undefined,
    params: (() => ApiQueryParams | undefined) | undefined,
    options: ApiGetResourceOptions<T> & { defaultValue: T }
  ): HttpResourceRef<T>;
  getResource<T>(
    endpoint: () => string | undefined,
    params?: () => ApiQueryParams | undefined,
    options?: ApiGetResourceOptions<T>
  ): HttpResourceRef<T | undefined>;
  getResource<T>(
    endpoint: () => string | undefined,
    params?: () => ApiQueryParams | undefined,
    options?: ApiGetResourceOptions<T>
  ): HttpResourceRef<T | undefined> {
    return httpResource<T>(
      () => {
        const ep = endpoint();
        if (!ep) {
          return undefined;
        }

        const url = this.getUrl(ep);
        const queryParams = params?.();
        Console.log(`[${this.log_label}] GET Resource Request to:`, url, 'info');
        if (queryParams) {
          Console.log(`[${this.log_label}] GET Resource Params:`, queryParams, 'info');
        }

        return {
          url,
          headers: this.getHeaders(),
          params: queryParams ? this.buildParams(queryParams) : undefined
        };
      },
      {
        parse: (raw) => {
          const extracted = this.parseApiResponse<unknown>(raw);
          const result = options?.transform ? options.transform(extracted) : extracted;
          Console.log(`[${this.log_label}] GET Resource Response:`, result, 'info');
          return result as T;
        },
        defaultValue: options?.defaultValue,
        debugName: options?.debugName
      }
    );
  }

  /**
   * GET paginato reattivo basato su `httpResource` (Angular 22).
   * Ritorna la risposta completa `{ message, data, pagination }` come `getPaginated()`.
   */
  getPaginatedResource<T>(
    endpoint: () => string | undefined,
    pagination: () => IRequestPaginationData | undefined,
    extraParams: (() => ApiQueryParams | undefined) | undefined,
    options: ApiGetResourceOptions<IApiPaginatedStandardResponse<T>> & {
      defaultValue: IApiPaginatedStandardResponse<T>;
    }
  ): HttpResourceRef<IApiPaginatedStandardResponse<T>>;
  getPaginatedResource<T>(
    endpoint: () => string | undefined,
    pagination: () => IRequestPaginationData | undefined,
    extraParams?: () => ApiQueryParams | undefined,
    options?: ApiGetResourceOptions<IApiPaginatedStandardResponse<T>>
  ): HttpResourceRef<IApiPaginatedStandardResponse<T> | undefined>;
  getPaginatedResource<T>(
    endpoint: () => string | undefined,
    pagination: () => IRequestPaginationData | undefined,
    extraParams?: () => ApiQueryParams | undefined,
    options?: ApiGetResourceOptions<IApiPaginatedStandardResponse<T>>
  ): HttpResourceRef<IApiPaginatedStandardResponse<T> | undefined> {
    return httpResource<IApiPaginatedStandardResponse<T>>(
      () => {
        const ep = endpoint();
        const paginationData = pagination();
        if (!ep || !paginationData) {
          return undefined;
        }

        const url = this.getUrl(ep);
        const allParams = this.buildPaginationParams(paginationData, extraParams?.());
        Console.log(`[${this.log_label}] GET Paginated Resource Request to:`, url, 'info');
        Console.log(`[${this.log_label}] Pagination Resource Params:`, allParams, 'info');

        return {
          url,
          headers: this.getHeaders(),
          params: this.buildParams(allParams)
        };
      },
      {
        parse: (raw) => {
          Console.log(`[${this.log_label}] GET Paginated Resource Response:`, raw, 'info');
          return raw as IApiPaginatedStandardResponse<T>;
        },
        defaultValue: options?.defaultValue,
        debugName: options?.debugName
      }
    );
  }

  /**
   * Effettua una richiesta HTTP GET
   * @param endpoint - L'endpoint dell'API da chiamare
   * @param params - Parametri query opzionali
   * @returns Observable contenente la risposta dell'API
   * @throws IApiErrorResponse in caso di errore
   */
  get<T>(endpoint: string, params?: ApiQueryParams): Observable<T> {
    const url = this.getUrl(endpoint);
    Console.log(`[${this.log_label}] GET Request to:`, url, 'info');
    if (params) {
      Console.log(`[${this.log_label}] GET Params:`, params, 'info');
    }

    return this.http
      .get<IApiStandardResponse<T> | { message: string }>(url, {
        headers: this.getHeaders(),
        params: params ? this.buildParams(params) : undefined
      })
      .pipe(
        map((response: unknown): T => {
          Console.log(`[${this.log_label}] GET Response:`, response, 'info');
          return this.parseApiResponse<T>(response);
        }),
        catchError((error) => this.handleError(error))
      );
  }

  /**
   * Effettua una richiesta HTTP POST
   * @param endpoint - L'endpoint dell'API da chiamare
   * @param body - Il corpo della richiesta
   * @param customHeaders - Headers custom opzionali
   * @returns Observable contenente la risposta dell'API
   * @throws IApiErrorResponse in caso di errore
   */
  post<T, B = unknown>(endpoint: string, body?: B, customHeaders?: Record<string, string>): Observable<T> {
    const url = this.getUrl(endpoint);
    Console.log(`[${this.log_label}] POST Request to:`, url, 'info');
    if (body) {
      Console.log(`[${this.log_label}] POST Body:`, body, 'info');
    }

    return this.http
      .post<IApiStandardResponse<T> | { message: string }>(url, body, {
        headers: this.getHeaders(customHeaders)
      })
      .pipe(
        map((response: unknown): T => {
          Console.log(`[${this.log_label}] POST Response:`, response, 'info');
          return this.parseApiResponse<T>(response);
        }),
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Effettua una richiesta HTTP PUT
   * @param endpoint - L'endpoint dell'API da chiamare
   * @param body - Il corpo della richiesta
   * @returns Observable contenente la risposta dell'API (IApiPutResponse con message)
   * @throws IApiErrorResponse in caso di errore
   */
  put<B = unknown>(endpoint: string, body?: B): Observable<IApiPutResponse> {
    const url = this.getUrl(endpoint);
    Console.log(`[${this.log_label}] PUT Request to:`, url, 'info');
    if (body) {
      Console.log(`[${this.log_label}] PUT Body:`, body, 'info');
    }

    return this.http
      .put<IApiPutResponse>(url, body, {
        headers: this.getHeaders()
      })
      .pipe(
        map((response: IApiPutResponse) => {
          Console.log(`[${this.log_label}] PUT Response:`, response, 'info');
          return response;
        }),
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Effettua una richiesta HTTP PATCH
   * @param endpoint - L'endpoint dell'API da chiamare
   * @param body - Il corpo della richiesta
   * @returns Observable contenente la risposta dell'API
   * @throws IApiErrorResponse in caso di errore
   */
  patch<T, B = unknown>(endpoint: string, body?: B): Observable<T> {
    const url = this.getUrl(endpoint);
    Console.log(`[${this.log_label}] PATCH Request to:`, url, 'info');
    if (body) {
      Console.log(`[${this.log_label}] PATCH Body:`, body, 'info');
    }

    return this.http
      .patch<IApiStandardResponse<T> | { message: string }>(url, body, {
        headers: this.getHeaders()
      })
      .pipe(
        map((response: unknown): T => {
          Console.log(`[${this.log_label}] PATCH Response:`, response, 'info');
          return this.parseApiResponse<T>(response);
        }),
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Effettua una richiesta HTTP DELETE
   * @param endpoint - L'endpoint dell'API da chiamare
   * @param body - Corpo opzionale della richiesta (alcuni endpoint backend lo richiedono)
   * @returns Observable contenente la risposta dell'API
   * @throws IApiErrorResponse in caso di errore
   */
  delete<T, B = unknown>(endpoint: string, body?: B): Observable<T> {
    const url = this.getUrl(endpoint);
    Console.log(`[${this.log_label}] DELETE Request to:`, url, 'info');
    if (body) {
      Console.log(`[${this.log_label}] DELETE Body:`, body, 'info');
    }

    return this.http
      .delete<IApiStandardResponse<T> | { message: string }>(url, {
        headers: this.getHeaders(),
        body
      })
      .pipe(
        map((response: unknown): T => {
          Console.log(`[${this.log_label}] DELETE Response:`, response, 'info');
          return this.parseApiResponse<T>(response);
        }),
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * GET request con paginazione
   * @param endpoint - L'endpoint dell'API da chiamare
   * @param paginationData - Dati di paginazione
   * @param params - Parametri aggiuntivi opzionali
   * @returns Observable contenente la risposta paginata dell'API
   * @throws IApiErrorResponse in caso di errore
   */
  getPaginated<T>(
    endpoint: string,
    paginationData: IRequestPaginationData,
    params?: ApiQueryParams
  ): Observable<IApiPaginatedStandardResponse<T>> {
    const allParams = this.buildPaginationParams(paginationData, params);

    const url = this.getUrl(endpoint);
    Console.log(`[${this.log_label}] GET Paginated Request to:`, url, 'info');
    Console.log(`[${this.log_label}] Pagination Params:`, allParams, 'info');

    return this.http
      .get<IApiPaginatedStandardResponse<T>>(url, {
        headers: this.getHeaders(),
        params: this.buildParams(allParams)
      })
      .pipe(
        map((response: IApiPaginatedStandardResponse<T>) => {
          Console.log(`[${this.log_label}] GET Paginated Response:`, response, 'info');
          return response;
        }),
        catchError(this.handleError.bind(this))
      );
  }
}
